import { useCallback, useState } from "react";

/**
 * Persists an arbitrary JSON-serialisable preference per page in localStorage.
 * Mirrors the useState API (supports updater functions) so it can drop in for
 * page-level UI state that should survive reloads — e.g. table columns or the
 * active sort.
 *
 * @param {string} pageId - Stable page identifier.
 * @param {string} name - Preference name (namespaced under the pageId).
 * @param {*} defaultValue - Value used when nothing is stored / on parse error.
 * @returns {[*, function]} Current value and a persisting setter.
 */
export default function usePagePref(pageId, name, defaultValue) {
  const storageKey = `${pageId}-${name}`;

  const [value, setValueState] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored != null ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setValue = useCallback(
    (next) => {
      setValueState((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        try {
          localStorage.setItem(storageKey, JSON.stringify(resolved));
        } catch {
          /* ignore storage errors (e.g. private mode) */
        }
        return resolved;
      });
    },
    [storageKey],
  );

  return [value, setValue];
}
